// Miky Motorsport - AI souperi (M2)
// AIController: pohani Vehicle stejnou fyzikou jako hrac (fer). Sleduje racing line
// s lookaheadem, regulu je rychlost dle zakriveni trati, rubber-band, recovery.
import * as THREE from 'three';
import { clamp } from './physics/tires.js';
import { MS_TO_KMH } from './constants.js';
import { AI_CURV_WINDOWS, curvatureFrom, targetSpeedFor, throttleBrakeFor, steerTo } from './aiMath.js';

export class AIController {
  constructor(vehicle, track, opts = {}) {
    this.v = vehicle;
    this.track = track;
    this.skill = opts.skill ?? 0.92; // 0..1 (ovlivnuje tempo a grip vyuziti)
    // Strop primociare rychlosti = top speed auta (m/s) s malou rezervou, aby AI na
    // dlouhych rovinkach nezustavala pozadu u rychlych aut (top speed 268-318 km/h).
    // Fallback na velkorysou hodnotu, kdyz spec nema topSpeedKmh.
    const topKmh = vehicle.spec?.topSpeedKmh ?? 320;
    this.maxStraightMs = (topKmh / MS_TO_KMH) * 1.02;
    this.pace = opts.pace ?? 1.0; // obtiznostni skalovani cilove rychlosti (M4)
    this.throttleCap = opts.throttle ?? 1.0; // agresivita plynu (M4)
    this.laneOffset = opts.laneOffset ?? 0; // bocni posun na trati (rozprostreni)
    this._q = new THREE.Quaternion();
    this._fwd = new THREE.Vector3();
    this._stuck = 0;
    this._recovering = 0; // doba zbyvajici recovery (drzi rezim min. dobu)
    this._speedScale = 1; // rubber-band (nastavuje RaceManager)
  }

  setRubber(scale) {
    this._speedScale = scale;
  }

  // Vraci controls {throttle, brake, steer, handbrake}
  computeControls(dt) {
    const v = this.v;
    const t = v.body.translation();
    const r = v.body.rotation();
    this._q.set(r.x, r.y, r.z, r.w);
    this._fwd.set(0, 0, 1).applyQuaternion(this._q);
    const speed = v.speedMs;
    const speedKmh = Math.abs(speed) * MS_TO_KMH;

    const near = this.track.nearest(t.x, t.z);
    const offTrack = Math.abs(near.lateral) > 9.5;

    // --- Lookahead cil (vc. bocniho posunu) ---
    const lookahead = 9 + Math.abs(speed) * 0.55;
    const tp = this.track.pointAtS(near.s + lookahead);
    const tgtX = tp.x + tp.nx * this.laneOffset;
    const tgtZ = tp.z + tp.nz * this.laneOffset;

    // --- Rizeni k cili ---
    let dx = tgtX - t.x, dz = tgtZ - t.z;
    const dl = Math.hypot(dx, dz) || 1;
    dx /= dl; dz /= dl;
    const fwdAng = Math.atan2(this._fwd.x, this._fwd.z);
    const tgtAng = Math.atan2(dx, dz);
    let steer = steerTo(fwdAng, tgtAng); // steer +1 = vpravo

    // --- Rychlost dle NEJOSTREJSI zatacky v dohledu ---
    // Vzorkuj nekolik oken (blizko/stred/dal) a vezmi MAX zakriveni - aby AI vcas
    // zpomalila i na kratke ostre zatacky (predtim sirsi okno ostre zatacky "rozmazalo").
    const curvature = curvatureFrom(this.track, near.s, AI_CURV_WINDOWS);
    // vyuziti gripu v zatackach - vice zavisle na skillu (aby obtiznost mela efekt)
    // strop = top speed auta (m/s), pak obtiznostni pace + rubber-band
    let targetSpeed = targetSpeedFor(curvature, this.skill, this.maxStraightMs) * this.pace * this._speedScale;

    // --- Plyn / brzda ---
    const tb = throttleBrakeFor(speed, targetSpeed);
    let throttle = tb.throttle, brake = tb.brake;

    // --- Off-track: zpomal a vrat se ---
    if (offTrack) {
      throttle = Math.min(throttle, 0.4);
      steer = clamp(steer * 1.3, -1, 1);
    }

    // --- Stuck recovery (drzi se min. dobu, jinak osciluje: couvani zvysi speedKmh
    //     -> okamzity navrat na forward -> zase do zdi) ---
    if (this._recovering > 0) {
      this._recovering -= dt;
      return recoveryControls(steer);
    }
    if (speedKmh < 4) this._stuck += dt;
    else this._stuck = Math.max(0, this._stuck - dt * 0.5);
    if (this._stuck > 1.5) {
      this._recovering = 1.2; // min. doba couvani
      this._stuck = 0;
      return recoveryControls(steer);
    }

    throttle *= this.throttleCap; // obtiznostni agresivita
    return { throttle, brake, steer, handbrake: false };
  }
}

// Couvaci recovery z zaseknuti: plny brzdny moment, rejd opacne (predek se stoci
// zpet k cili na trati). steer = aktualni cilovy rejd, ktery se invertuje.
function recoveryControls(steer) {
  return { throttle: 0, brake: 1, steer: clamp(-steer * 1.2, -1, 1), handbrake: false };
}
